import { Component, Input, OnChanges } from "@angular/core";

interface GraphNode {
  id: string;
  weight: number;
  x: number;
  y: number;
  r: number;
}

interface GraphEdge {
  source: GraphNode;
  target: GraphNode;
  corr: number;
  opacity: number;
  width: number;
}

@Component({
  selector: "app-exp-cov-graph",
  standalone: true,
  imports: [],
  template: `
    <div class="cov-graph">
      <svg
        [attr.viewBox]="'0 0 ' + width + ' ' + height"
        preserveAspectRatio="xMidYMid meet"
      >
        @for (e of edges; track e) {
          <line
            class="edge"
            [attr.x1]="e.source.x"
            [attr.y1]="e.source.y"
            [attr.x2]="e.target.x"
            [attr.y2]="e.target.y"
            [attr.stroke]="e.corr >= 0 ? '#7C5CFF' : '#FF5C7A'"
            [attr.stroke-opacity]="e.opacity"
            [attr.stroke-width]="e.width"
          />
        }
        @for (n of nodes; track n) {
          <g>
            <circle
              class="node"
              [attr.cx]="n.x"
              [attr.cy]="n.y"
              [attr.r]="n.r"
            ></circle>
            <text
              class="node-label"
              [attr.x]="n.x"
              [attr.y]="n.y + n.r + 12"
              text-anchor="middle"
            >
              {{ n.id }}
            </text>
          </g>
        }
      </svg>
    </div>
  `,
})
export class CovGraphComponent implements OnChanges {
  @Input() labels: string[] = [];
  @Input() weights: number[] = [];
  @Input() matrix: number[][] = [];
  @Input() threshold = 0.2;
  @Input() width = 560;
  @Input() height = 420;

  nodes: GraphNode[] = [];
  edges: GraphEdge[] = [];

  ngOnChanges(): void {
    const n = this.labels.length;
    if (!n) {
      this.nodes = [];
      this.edges = [];
      return;
    }
    const cx = this.width / 2;
    const cy = this.height / 2;
    const radius = Math.min(this.width, this.height) * 0.35;
    const maxW = Math.max(...this.weights, 0.0001);

    this.nodes = this.labels.map((id, i) => {
      const theta = (i / n) * Math.PI * 2 - Math.PI / 2;
      const weight = this.weights[i] || 0;
      const r = 8 + (weight / maxW) * 18;
      return {
        id,
        weight,
        x: cx + Math.cos(theta) * radius,
        y: cy + Math.sin(theta) * radius,
        r,
      };
    });

    const edges: GraphEdge[] = [];
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const v = this.matrix[i]?.[j];
        if (v === undefined || v === null || !isFinite(v)) continue;
        if (Math.abs(v) < this.threshold) continue;
        edges.push({
          source: this.nodes[i],
          target: this.nodes[j],
          corr: v,
          opacity: 0.2 + Math.min(0.75, Math.abs(v) * 0.9),
          width: 0.5 + Math.abs(v) * 2.5,
        });
      }
    }
    this.edges = edges;
  }
}
